import { Dispatch, SetStateAction } from 'react';
import { Box, Heading, Divider } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { lowerCase } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { GROUP_TYPE } from 'utils/constants';

type Props = {
  groups: any;
  setPage: Dispatch<SetStateAction<number>>;
};

type GroupListProps = {
  data: any[];
  groupType: number;
  title: string;
};

const copyStyles = {
  gap: 'padding_8',
};

const headingStyle = {
  mb: 'padding_8',
};

const subHeadingStyle = {
  color: Colors.ArmyGreen500,
  mt: 'padding_32',
};

const typeHeading = {
  fontSize: '16px',
  fontWeight: '600',
};

const bodyText = {
  fontSize: '13px',
};

const iconBox = {
  color: Colors.ForestGreen900,
  height: 'auto',
};

const dividerStyle = {
  mt: 'padding_24',
};

const parentContainer = { width: '100%' };

const titleRow = {
  justifyContent: 'left',
  width: '100%',
  gap: 'padding_8',
  mb: 'padding_24',
  cursor: 'pointer',
};

const groupsColumn = { width: '100%', justifyContent: 'center' };

const itemContainer = { mt: 'padding_8', mb: 'padding_8' };

const frequencyText = {
  gap: 'padding_8',
};

const GroupList = ({ data, groupType, title }: GroupListProps) => {
  const navigate = useNavigate();
  const handleGroupEdit = (groupId: string) => {
    navigate(`/saved-search/${groupId}/edit/`);
  };

  return (
    <>
      <Text.SmallLabel sx={subHeadingStyle}>{title}</Text.SmallLabel>
      <Divider my="4" />
      {data
        .filter((group: any) => group.type === groupType)
        .map((group: any) => (
          <Box key={group?.id} sx={itemContainer}>
            <Heading as="h4" sx={typeHeading}>
              {group?.name}
            </Heading>
            <Text sx={bodyText}>{group?.description}</Text>
            <FlexRow sx={copyStyles}>
              <FlexRow sx={frequencyText}>
                <Icon name={Icon.Name.Email} />
                {group?.is_notification_enabled ? (
                  <Text>
                    Sending notifications {lowerCase(group.email_frequency) || 'daily'}
                  </Text>
                ) : (
                  <Text>Notifications disabled</Text>
                )}
              </FlexRow>
              <Button
                variant="unstyled"
                onClick={() => handleGroupEdit(group?.id)}
                sx={iconBox}
                endAdornment={<Icon name={Icon.Name.ChevronRight} />}
              >
                <Text.BodyBold>Edit</Text.BodyBold>
              </Button>
            </FlexRow>
            <Divider sx={dividerStyle} />
          </Box>
        ))}
    </>
  );
};

export const ProjectGroups = ({ groups, setPage }: Props) => {
  return (
    <Box sx={parentContainer}>
      <FlexRow sx={titleRow} onClick={() => setPage(0)}>
        <Icon fill={Colors.ForestGreen900} name={Icon.Name.ChevronLeft} />
        <Text.BodyBold>Notification Settings</Text.BodyBold>
      </FlexRow>
      <Text.H2 sx={headingStyle}>Saved Groups</Text.H2>
      <Text color={Colors.TextQuiet}>
        Manage your settings to customize how you are notified about projects.
      </Text>
      <FlexColumn sx={groupsColumn}>
        <GroupList data={groups} groupType={GROUP_TYPE.PROJECT} title="Project Groups" />
        <GroupList data={groups} groupType={GROUP_TYPE.COMPANY} title="Company Groups" />
      </FlexColumn>
    </Box>
  );
};
